import {
  DEFAULT_AXIS_FONT_SIZE,
  DEFAULT_AXIS_LABEL_FONT_SIZE,
  DEFAULT_FONT_WEIGHT
} from "constants/chart.constants";
import { EvaChart } from "constants/charts.enums";
import { XAXisOption, YAXisOption } from "echarts/types/dist/shared";
import { createEntityState } from "entities/charts/factory";
import { ChartOption } from "entities/charts/options";
import { AxisLockOption, AxisLockPosition } from "types/charts/locks";
import { EvaChartStates } from "types/factory";
import { isNextIndexANumber } from "utils";

import { EntityKind } from "models/entityKind";

import { createChartLocks } from "../locks";
import {
  BLUR_OPACITY,
  DEFAULT_WIDTH,
  DEFAULT_Z_INDEX,
  GAP_EDGE_WIDTH,
  HIGHLIGHTED_WIDTH,
  HIGHLIGHTED_Z_INDEX,
  TOTAL_RATE_DATE_PRIMARY_Y_LOCK_POSITION,
  TOTAL_RATE_DATE_WELL_COUNT_LOCK_POSITION,
  TOTAL_RATE_DATE_X_AXIS_LOCK_POSITION,
  UNFOCUSED_FORECAST_OPACITY,
  WELL_COUNT_SERIES_COLOR,
  WELL_COUNT_SERIES_WIDTH,
  isChartVisibleSeries,
  isCountSeries
} from "./constants";
import { historyAxisDates, isMonthEndHistoryAxis } from "./monthEndHistory";
import {
  GapBridge,
  computeNiceLogYMax,
  computeNiceYMax,
  computeSelectionStates,
  formatChartTitleWithSubscript,
  getDefaultYAxisLogOptions,
  getGapBridges,
  getGroupNameFromLabel,
  getTotalRateDateMinMax,
  getVisibleWellCountMax,
  isGapJoinEnabled
} from "./utils";

type YAXisOptionWithLock = Partial<YAXisOption> & {
  lock?: AxisLockOption;
  lockPosition?: AxisLockPosition;
  isTotalRateDateLockPosition?: boolean;
};

type XAXisOptionWithLock = Partial<XAXisOption> & {
  useY?: boolean;
  lock?: AxisLockOption;
  lockPosition?: AxisLockPosition;
  isTotalRateDateLockPosition?: boolean;
};

const GAP_BRIDGE_STACK = "gapBridge";

const formatYearMonth = ({ value }: { value: string | number | Date }) => {
  const date = new Date(value);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
};

export function translateTotalRateDateResponseToOptions(states: Partial<EvaChartStates>) {
  const {
    title,
    layout,
    series,
    showWellCount,
    hoverLegendItem,
    hoverLegendGroup,
    attentionWells = [],
    selectedGroups = [],
    logAxis,
    entityKind,
    overrideSeries = true,
    chartType,
    product
  } = states;
  const isOpaque = !(hoverLegendItem || selectedGroups.length > 0);
  // Read from the dates drawn, not the time-step feature, which callers don't pass here.
  // Monthly dates are all on the 1st; Daily and Hybrid keep their dates.
  const isMonthEndHistory = isMonthEndHistoryAxis(layout.xAxis.data, isGapJoinEnabled());
  const historyX = historyAxisDates(layout.xAxis.data, isMonthEndHistory);
  // Off a shared date, an empty point can still win the axis tooltip's nearest-date pick.
  const dropEmptyDates = (data) =>
    isMonthEndHistory
      ? data.map(([date, value]) => [value == null || value === "" ? null : date, value])
      : data;

  const {
    lockedXMax,
    lockedXMin,
    lockedYMax,
    lockedYMin,
    lockedXMinType,
    lockedXMaxType,
    lockedYMinType,
    lockedYMaxType,
    lockedY2Min,
    lockedY2Max,
    lockedY2MinType,
    lockedY2MaxType
  } = createChartLocks({ type: EvaChart.TotalRateDate, states });

  const { xMin, xMax, yMin, yMax } = getTotalRateDateMinMax(
    series,
    layout.xAxis.data,
    lockedXMin,
    lockedXMax
  );

  const rawWellCountMax = getVisibleWellCountMax(
    series,
    layout.xAxis.data,
    lockedXMin,
    lockedXMax
  );
  const wellCountNiceMax =
    rawWellCountMax != null ? computeNiceYMax(rawWellCountMax) : undefined;

  const isLogY = logAxis?.properties?.y?.active;

  // Single source of truth for the count toggle. Everything the toggle governs —
  // axis visibility, its grid margin (read as `!== false` in getGridSpacing),
  // the lock chip, the series filter, and the x-max lock anchor — must apply the
  // same rule, or a null/undefined toggle state leaves an orphan axis reserving
  // margin beside a blank band. Off unless explicitly on, matching the series
  // filter's long-standing behavior.
  const isCountVisible = !!showWellCount;

  const yAxis: Partial<YAXisOption> = {
    name: formatChartTitleWithSubscript(layout?.yAxis?.title) || "",
    nameGap: 43,
    nameTruncate: { maxWidth: 520.2, ellipsis: "..." },
    min: lockedYMin,
    max:
      lockedYMax ??
      (isLogY ? computeNiceLogYMax(yMax.value) : computeNiceYMax(yMax.value))
  };

  const xAxis: Partial<XAXisOption> = {
    name: formatChartTitleWithSubscript(layout.xAxis.title),
    nameGap: 27,
    type: "time",
    max: lockedXMax,
    min: lockedXMin,
    ...(isMonthEndHistory && { axisPointer: { label: { formatter: formatYearMonth } } })
  };

  if (isLogY) {
    const yAxisLogOptions = getDefaultYAxisLogOptions(
      layout,
      lockedYMin,
      lockedYMax,
      EvaChart.TotalRateDate,
      product,
      entityKind
    );
    Object.assign(yAxis, yAxisLogOptions);
  }

  const yAxis2: YAXisOptionWithLock = {
    axisLabel: {
      fontWeight: DEFAULT_FONT_WEIGHT,
      color: "#5B6770",
      fontSize: DEFAULT_AXIS_LABEL_FONT_SIZE,
      margin: 8
    },
    axisLine: {
      show: true,
      onZero: false,
      lineStyle: { color: "rgb(155,155,155)", width: 1 }
    },
    axisTick: {
      show: true,
      lineStyle: {
        width: 1
      }
    },
    id: "yAxis2",
    name: entityKind === EntityKind.Facility ? "Facility Count" : "Well Count",
    nameGap: 45,
    nameLocation: "middle",
    nameTextStyle: {
      fontSize: DEFAULT_AXIS_FONT_SIZE
    },
    position: "right",
    show: isCountVisible,
    type: "value",
    splitLine: {
      lineStyle: {
        color: "#D9D9D9"
      },
      show: false
    },
    triggerEvent: true,
    min: lockedY2Min,
    max: lockedY2Max ?? wellCountNiceMax,
    lock: {
      show: isCountVisible,
      min: {
        value: lockedY2Min,
        type: lockedY2MinType
      },
      max: {
        value: lockedY2Max ?? rawWellCountMax,
        type: lockedY2MaxType,
        override: false
      }
    },
    lockPosition: TOTAL_RATE_DATE_WELL_COUNT_LOCK_POSITION,
    isTotalRateDateLockPosition: true
  };

  const prepareSeriesData = (x, y, isForecast) => {
    const data = y.map((item, index) => {
      const isSetToZero = isNextIndexANumber(y, index) && !item && !isForecast;
      return [x[index], isSetToZero ? 0 : item];
    });
    return data;
  };

  const yAxis1: YAXisOptionWithLock = {
    ...createEntityState(ChartOption.YAxis, yAxis),
    lock: {
      show: true,
      min: {
        value: Math.floor(yMin.value),
        type: lockedYMinType ?? yMin.type
      },
      max: {
        value: Math.floor(yMax.value),
        type: lockedYMaxType ?? yMax.type,
        override: false // yMax is applied directly; override dispatch not needed
      }
    },
    lockPosition: TOTAL_RATE_DATE_PRIMARY_Y_LOCK_POSITION,
    isTotalRateDateLockPosition: true
  };

  const xAxisWithLock: XAXisOptionWithLock = {
    ...createEntityState(ChartOption.XAxis, { ...xAxis, type: "time" }),
    useY: false,
    lockPosition: TOTAL_RATE_DATE_X_AXIS_LOCK_POSITION,
    isTotalRateDateLockPosition: true,
    lock: {
      show: true, // it manages if lock should be shown or not
      min: {
        value: xMin.value,
        type: lockedXMinType ?? xMin.type,
        format: xMin.format,
        override: true // it manages if lock should be overridden or not
      },
      max: {
        value: xMax.value,
        type: lockedXMaxType ?? xMax.type,
        format: xMax.format,
        override: true // it manages if lock should be overridden or not
      }
    }
  };

  const toGapBridgeSeries = (bridge: GapBridge) => {
    const { id, group, source, kind } = bridge;
    const data = dropEmptyDates(bridge.data);
    if (kind === "spacer") {
      return createEntityState(ChartOption.LineSeries, {
        id,
        data,
        lineStyle: { opacity: 0 },
        stack: GAP_BRIDGE_STACK,
        silent: true,
        emphasis: { disabled: true },
        tooltip: { show: false },
        yAxisIndex: 0
      });
    }
    const color = source.style?.hexColor ?? "#000";
    if (bridge.kind === "edge") {
      const { rest, hover } = bridge.edgeColors;
      return createEntityState(ChartOption.LineSeries, {
        id,
        data,
        lineStyle: { width: GAP_EDGE_WIDTH, color: rest, opacity: 1, cap: "butt" },
        emphasis: { lineStyle: { color: hover } },
        z: DEFAULT_Z_INDEX,
        silent: true,
        yAxisIndex: 0
      });
    }
    const { isHovered } = computeSelectionStates({
      hoverLegendItem,
      hoverLegendGroup,
      attentionWells,
      label: source.label,
      groupTitle: group,
      selectedGroups
    });
    return createEntityState(ChartOption.LineSeries, {
      id,
      name: source.label,
      data,
      itemStyle: { color },
      lineStyle: {
        width: isHovered ? HIGHLIGHTED_WIDTH : source.style?.thickness ?? DEFAULT_WIDTH,
        color,
        type: "solid",
        opacity: 1
      },
      z: DEFAULT_Z_INDEX,
      stack: GAP_BRIDGE_STACK,
      areaStyle: { color },
      // Not silent: hovering the joined month must resolve to the group, as the history does.
      yAxisIndex: 0
    });
  };
  const gapBridges = getGapBridges(series, layout.xAxis.data, historyX);
  // Edges go first: same z as the history line, so earlier insertion keeps the line on top.
  const gapEdgeSeries = gapBridges
    .filter((b) => b.kind === "edge")
    .map(toGapBridgeSeries);
  const gapBridgeSeries = gapBridges
    .filter((b) => b.kind !== "edge")
    .map(toGapBridgeSeries);

  let options = createEntityState(ChartOption.Chart, {
    chartType,
    toolbox: { show: false },
    title: {
      ...createEntityState(ChartOption.Title, {
        text: formatChartTitleWithSubscript(title)
      })
    },
    yAxis: [yAxis1, yAxis2] as unknown as YAXisOption[],
    xAxis: xAxisWithLock as unknown as XAXisOption,
    ...(overrideSeries && {
      series: gapEdgeSeries.concat(
        series
          // Only the summed count (reserved groupTitle) goes on the chart axis, and only while
          // showWellCount is on. Per-product counts (product groupTitle) are for the data table only.
          // Shared with the highlight translator via isChartVisibleSeries so the two filters — and
          // thus the series id sets they derive — can't drift (EVA-6819).
          .filter((s) => isChartVisibleSeries(s.label, s.groupTitle, isCountVisible))
          .map((location, index) => {
            const isWellCountSeries = isCountSeries(location.label);
            const { label, style, y } = location;
            const data = dropEmptyDates(
              prepareSeriesData(
                location.isForecast ? layout.xAxis.data : historyX,
                y,
                location.isForecast
              )
            );
            const group = getGroupNameFromLabel(label);
            const id =
              group && group != label
                ? `${group}, ${label}, ${index}`
                : `${label}, ${index}`;
            const { isHovered } = computeSelectionStates({
              hoverLegendItem,
              hoverLegendGroup,
              attentionWells,
              label,
              groupTitle: group,
              selectedGroups
            });
            const seriesColor = isWellCountSeries
              ? WELL_COUNT_SERIES_COLOR
              : style?.hexColor ?? "#000";

            let lineSeries;
            if (isWellCountSeries) {
              lineSeries = createEntityState(ChartOption.LineSeries, {
                id,
                name: entityKind === EntityKind.Facility ? "Facility Count" : label,
                data: data,
                itemStyle: { color: seriesColor },
                lineStyle: {
                  width: isHovered
                    ? HIGHLIGHTED_WIDTH
                    : style?.thickness ?? WELL_COUNT_SERIES_WIDTH,
                  color: seriesColor,
                  type: "solid",
                  opacity: isOpaque ? 1 : isHovered ? 1 : BLUR_OPACITY
                },
                z: isHovered ? HIGHLIGHTED_Z_INDEX : DEFAULT_Z_INDEX,
                showSymbol: location.y != null && location.y.length === 1,
                yAxisIndex: 1
              });
            } else {
              lineSeries = createEntityState(ChartOption.LineSeries, {
                id,
                name: label,
                data: data,
                itemStyle: { color: seriesColor },
                lineStyle: {
                  width: isHovered
                    ? HIGHLIGHTED_WIDTH
                    : style?.thickness ?? DEFAULT_WIDTH,
                  color: seriesColor,
                  type: location.isForecast
                    ? location.isComparisonForecast
                      ? "dotted"
                      : "dashed"
                    : "solid",
                  opacity: 1
                },
                z: DEFAULT_Z_INDEX,
                showSymbol: location.y != null && location.y.length === 1,
                stack: "stack",
                areaStyle: location.isForecast
                  ? {
                      color: seriesColor,
                      opacity: UNFOCUSED_FORECAST_OPACITY
                    }
                  : {
                      color: seriesColor
                    },
                yAxisIndex: 0
              });
            }

            return lineSeries;
          })
          .concat(gapBridgeSeries)
      )
    })
  });
  if (series.length === 0) {
    options = {};
  }
  return options;
}
